'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CtaFinal from '../homepage/components/CtaFinal';

function useReveal(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = ref?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, [ref]);
}

const pains = [
  {
    title: 'Your closers are prospecting instead of closing',
    body: 'Every hour an AE spends dialing cold accounts is an hour of pipeline they are not converting. The most expensive prospectors in your company should not be prospecting.',
  },
  {
    title: 'Headcount is frozen but the target is not',
    body: 'The quota went up and the hiring plan did not. Outbound capacity has to come from somewhere that is not a requisition.',
  },
  {
    title: 'New segments need coverage you do not have',
    body: 'A new vertical, region, or product line deserves dedicated outbound attention that your current team cannot spare.',
  },
];

const cards = [
  {
    tag: 'Divide',
    title: 'Volume to us, strategy to you',
    body: 'Industry research on B2B sales teams found the hybrid model, outsourced reps for outbound volume with in-house sellers on strategic accounts, outperforms either approach alone for growing companies. We slot into exactly that structure.',
  },
  {
    tag: 'Integrate',
    title: 'Fits your existing motion',
    body: 'Your reps work your ICP, your messaging standards, and your CRM process, and hand meetings to your closers with full context. Weekly reporting keeps your sales leadership in control of the whole funnel.',
  },
  {
    tag: 'Flex',
    title: 'Capacity that moves with the plan',
    body: 'Add a rep for a new territory, a launch, or a busy season. Month-to-month terms mean the team scales up and down with the plan instead of with the org chart.',
  },
];

export default function ExpandPage() {
  const rightRef = useRef<HTMLDivElement>(null);
  const sec1 = useRef<HTMLDivElement>(null);
  useReveal(rightRef);
  useReveal(sec1);

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <header
          className="py-24"
          style={{
            background:
              'radial-gradient(900px 500px at 85% -10%, rgba(201,137,65,0.15), transparent 60%), var(--navy-deep)',
          }}
        >
          <div className="max-w-site mx-auto px-7">
            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-16 items-start">
              <div>
                <div className="inline-flex font-mono text-[12px] tracking-[0.18em] uppercase text-primary border border-primary/50 px-4 py-2 rounded-full mb-6">
                  For teams extending their sales org
                </div>
                <h1 className="heading-land text-white">More pipeline without more headcount</h1>
                <p className="mt-5 text-[19px] text-muted-cream leading-relaxed max-w-[56ch]">
                  Your internal team is good. It is also full. CommandVA adds dedicated, phone-first outbound capacity beside your existing sellers, feeding your closers qualified meetings while they stay focused on revenue.
                </p>
                <div className="flex flex-wrap gap-3.5 mt-9">
                  <a
                    href="/request-meeting"
                    className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] bg-primary text-navy-deep border border-transparent transition-all duration-200 hover:bg-gold-bright hover:-translate-y-px hover:shadow-gold"
                  >
                    Book a strategy call
                  </a>
                  <Link
                    href="/pricing"
                    className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] border border-line-navy text-white transition-all duration-200 hover:border-primary hover:text-primary"
                  >
                    See pricing
                  </Link>
                </div>
              </div>

              <div ref={rightRef} className="pt-4">
                {pains.map((p, i) => (
                  <div
                    key={p.title}
                    className="rev pain-item"
                    style={{ transitionDelay: `${i * 0.1}s` }}
                  >
                    <b className="block text-[16.5px] mb-1 text-white font-semibold">{p.title}</b>
                    <p className="text-[15px] text-muted-cream leading-relaxed">{p.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* Hybrid model */}
        <section ref={sec1} className="py-24">
          <div className="max-w-site mx-auto px-7">
            <div className="rev mb-13 max-w-[780px]">
              <div className="eyebrow">The hybrid model</div>
              <h2 className="heading-2 text-ink">Your team closes. Ours opens.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {cards.map((card, i) => (
                <div
                  key={card.tag}
                  className="rev bg-white border border-line-color rounded-brand p-7"
                  style={{ transitionDelay: `${i * 0.09}s` }}
                >
                  <span className="block font-mono text-[11px] tracking-[0.18em] text-primary uppercase mb-3.5">
                    {card.tag}
                  </span>
                  <h3 className="heading-3 text-ink mb-3">{card.title}</h3>
                  <p className="text-[15px] text-muted-text leading-relaxed">{card.body}</p>
                </div>
              ))}
            </div>
            <div className="rev gold-strip mt-8" style={{ transitionDelay: '0.27s' }}>
              <div>
                <b className="text-primary">The budget line writes itself:</b> a dedicated CommandVA rep runs about a third of the roughly $10,000 fully loaded cost of an internal hire, with no requisition, no recruiting cycle, and no ramp on your payroll.
              </div>
            </div>
          </div>
        </section>

        <CtaFinal
          headline="Add outbound capacity this month"
          body="Bring your coverage gaps to a 30-minute call. We will map where dedicated reps fit beside your team."
          cta="Book a strategy call"
        />
      </main>
      <Footer />
    </>
  );
}
